/**
 * OutboundPanel — destination webhook list (toggle/remove, event chips,
 * secret + status) + add-destination form (url + event chips + add).
 */
import React from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../../../theme';
import { Trash } from '../../../icons';
import { Toggle } from '../../../components';
import { useBridge } from '../../../store/bridgeStore';
import { OUT_EVENTS } from '../../../data/seeds';

export default function OutboundPanel({ adapterId, vendorName }: { adapterId: string; vendorName: string }) {
  const { state, actions } = useBridge();
  const hooks = state.outboundHooks[adapterId] || [];
  const slug = vendorName.toLowerCase().replace(/[^a-z0-9]/g, '');

  return (
    <View style={{ gap: 12 }}>
      <Text style={styles.intro}>
        SK Bridge POSTs canonical events to these destinations when {vendorName} data changes. Use this to forward to another app's webhook.
      </Text>

      {hooks.length > 0 && (
        <View style={{ gap: 8 }}>
          {hooks.map(h => (
            <View key={h.id} style={[styles.hook, { borderLeftColor: h.active ? colors.success : colors.ink4 } as any]}>
              <View style={styles.hookTop}>
                <Text className="t-mono" style={[styles.hookUrl, { flex: 1 }]} numberOfLines={1}>{h.url}</Text>
                <Toggle small on={h.active} onToggle={() => actions.toggleOutbound(adapterId, h.id)} trackOn={colors.success} />
                <Pressable onPress={() => actions.removeOutbound(adapterId, h.id)} style={{ padding: 2, cursor: 'pointer' } as any} ><Trash size={14} color={colors.ink3} /></Pressable>
              </View>
              <View style={styles.chips}>
                {h.events.map(e => <View key={e} style={styles.chip}><Text style={styles.chipText}>{slug + '.' + e}</Text></View>)}
              </View>
              <View style={styles.hookBottom}>
                <Text className="t-mono" style={styles.secret} numberOfLines={1}>{h.secret}</Text>
                <Text style={styles.statusLabel}>{h.active ? 'active · signed' : 'paused'}</Text>
              </View>
            </View>
          ))}
        </View>
      )}

      <View style={styles.addBox}>
        <Text style={styles.heading}>Add destination</Text>
        <TextInput
          value={state.outDraftUrl}
          onChangeText={actions.setOutDraftUrl}
          placeholder="https://app.example.com/webhooks/sk"
          placeholderTextColor={colors.ink4 as any}
          style={styles.input}
        />
        <View>
          <Text style={styles.sendOn}>Send on</Text>
          <View style={styles.chips}>
            {OUT_EVENTS.map(ev => {
              const on = state.outDraftEvents.includes(ev);
              return (
                <Pressable
                  key={ev}
                  onPress={() => actions.toggleDraftEvent(ev)}
                  style={[styles.evChip, { borderColor: on ? colors.accent : colors.line, backgroundColor: on ? colors.accentSoft : colors.card } as any]}
                >
                  <Text className="t-mono" style={[styles.evChipText, { color: on ? colors.accentInk : colors.ink3 } as any]}>{ev}</Text>
                </Pressable>
              );
            })}
          </View>
        </View>
        <Pressable onPress={() => actions.addOutbound(adapterId)} style={styles.addBtn}><Text style={styles.addBtnText}>Add outbound webhook</Text></Pressable>
      </View>

      <View style={styles.infoBox}>
        <Text style={styles.infoText}>
          Payloads are signed with each destination's secret (HMAC-SHA256, <Text className="t-mono">X-SK-Signature</Text>). Failed deliveries retry with backoff.
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  intro: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, lineHeight: 19.5 },
  hook: { borderWidth: 1, borderColor: colors.line, borderLeftWidth: 3, borderRadius: radii.r2, backgroundColor: colors.paper2, paddingVertical: 11, paddingHorizontal: 12 },
  hookTop: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  hookUrl: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  chip: { paddingVertical: 2, paddingHorizontal: 7, borderRadius: radii.full, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card },
  chipText: { fontFamily: fontFamily.mono, fontSize: 10, color: colors.ink2 },
  hookBottom: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 6 },
  secret: { fontFamily: fontFamily.mono, fontSize: 10, color: colors.ink3, flex: 1 },
  statusLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3 },
  addBox: { borderWidth: 1, borderColor: colors.lineStrong, borderStyle: 'dashed', borderRadius: radii.r3, padding: 12, gap: 9 },
  heading: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.ink3, fontWeight: fontWeight.semibold },
  input: { width: '100%', height: 36, paddingHorizontal: 11, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, borderRadius: radii.r2, fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink as any, outlineStyle: 'none' } as any,
  sendOn: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginBottom: 5 },
  evChip: { paddingVertical: 4, paddingHorizontal: 10, borderRadius: radii.full, borderWidth: 1, cursor: 'pointer' } as any,
  evChipText: { fontFamily: fontFamily.mono, fontSize: fontSize.xs },
  addBtn: { height: 36, borderRadius: radii.r2, borderWidth: 1, borderColor: colors.accent, backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center', cursor: 'pointer' } as any,
  addBtnText: { fontFamily: fontFamily.sans, fontSize: fontSize.md, fontWeight: fontWeight.medium, color: '#fff' },
  infoBox: { padding: 10, paddingHorizontal: 12, borderWidth: 1, borderColor: colors.infoLine, backgroundColor: colors.infoSoft, borderRadius: radii.r2 },
  infoText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.info, lineHeight: 18 },
});
